import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CiSearch } from 'react-icons/ci';
import { BsCamera } from 'react-icons/bs';
import api from '../../../services/Api';
import ServiceType from './ServiceType';
import {
  showConfirmToast,
  showError,
  showLoading,
  showSuccess,
} from '../../Ui/ToastNotification';
import Title from '../../Title';
import toast from 'react-hot-toast';
import { BeatLoader } from 'react-spinners';
import { useSelector } from 'react-redux';

const TOAST_IDS = {
  FETCH_INDUSTRIES: 'fetch-industries',
  FETCH_SERVICES: 'fetch-services',
  ADD_SERVICE: 'add-service',
  UPDATE_SERVICE: 'update-service',
  DELETE_SERVICE: 'delete-service',
  CONFIRM_DELETE: 'confirm-delete',
};

const ServiceTypePage = () => {
  const { industryId } = useParams();
  const { userData } = useSelector((state) => state.auth);
  const userEmail = userData?.email;
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [serviceFormData, setServiceFormData] = useState({
    selectedIndustry: industryId || '',
    name: '',
    description: '',
    status: 'active',
    tags: [],
    newTag: '',
    previewImage: null,
    image: null,
  });

  const fetchIndustries = useCallback(async () => {
    const loadingToastId = { id: TOAST_IDS.FETCH_INDUSTRIES };
    try {
      const response = await api.get('/industryListing/industries');
      setAllCompanies(response.data.industries || []);
      toast.dismiss(loadingToastId);
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.dismiss(loadingToastId);
      showError(error.response?.data?.message || 'Failed to load industries', {
        id: TOAST_IDS.FETCH_INDUSTRIES,
      });
    }
  }, []);

  const fetchIndustryAndServices = useCallback(async () => {
    if (industryId === 'add' || !industryId) {
      setLoading(false);
      return;
    }

    const loadingToastId = { id: TOAST_IDS.FETCH_SERVICES };
    try {
      const [industryResponse, servicesResponse] = await Promise.all([
        api.get(`/industryListing/industries/${industryId}`),
        api.get(`/industryListing/service-types/industry/${industryId}`),
      ]);
      toast.dismiss(loadingToastId);
      showSuccess('Service loaded successfully!', {
        id: TOAST_IDS.FETCH_SERVICES,
      });
      setIndustry(industryResponse.data.industry);
      setServices(servicesResponse.data.industryWithServices || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError(error.response?.data?.message || 'Failed to load services', {
        id: TOAST_IDS.FETCH_SERVICES,
      });
    } finally {
      toast.dismiss(loadingToastId);
      setLoading(false);
    }
  }, [industryId]);

  useEffect(() => {
    fetchIndustries();
    fetchIndustryAndServices();
  }, [fetchIndustries, fetchIndustryAndServices]);

  useEffect(() => {
    if (selectedService) {
      setServiceFormData({
        selectedIndustry: selectedService.industry || industryId || '',
        name: selectedService.name || '',
        description: selectedService.description || '',
        status: selectedService.isActive ? 'active' : 'inactive',
        tags: selectedService.tags || [],
        newTag: '',
        previewImage: selectedService.imageUrl || null,
        image: null,
      });
    }
  }, [selectedService, industryId]);

  const handleImageChange = useCallback((event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      event.target.value = '';
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      showError(`File size (${sizeInMB}MB) exceeds 1MB limit.`);
      event.target.value = '';
      return;
    }

    // Preview
    const previewUrl = URL.createObjectURL(file);

    setServiceFormData((prev) => ({
      ...prev,
      image: file,
      previewImage: previewUrl,
    }));
  }, []);

  const handleAddTag = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const tag = serviceFormData.newTag.trim().replace(/,/g, '');
        if (tag && !serviceFormData.tags.includes(tag)) {
          setServiceFormData((prev) => ({
            ...prev,
            tags: [...prev.tags, tag],
            newTag: '',
          }));
        }
      }
    },
    [serviceFormData.newTag, serviceFormData.tags],
  );

  const handleRemoveTag = useCallback((tag) => {
    setServiceFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  }, []);

  const resetFormData = useCallback(() => {
    setServiceFormData({
      selectedIndustry: industryId || '',
      name: '',
      description: '',
      status: 'active',
      tags: [],
      newTag: '',
      previewImage: null,
      image: null,
    });
  }, [industryId]);

  const handleAddService = useCallback(async () => {
    if (!serviceFormData.name.trim()) {
      showError('Service name is required');
      return;
    }

    if (!serviceFormData.selectedIndustry) {
      showError('Please select an industry');
      return;
    }

    const loadingToastId = showLoading('Saving service...', {
      id: TOAST_IDS.ADD_SERVICE,
    });

    try {
      let imageUrl = serviceFormData.previewImage;

      if (serviceFormData.image && serviceFormData.image instanceof File) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', serviceFormData.image);

          const uploadResponse = await api.post(`/upload/file`, imageFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          imageUrl = uploadResponse.data.secure_url || uploadResponse.data.url;

          if (!imageUrl) {
            console.error('Upload response:', uploadResponse.data);
            showError('Failed to get image URL from upload response');
            toast.dismiss(loadingToastId);
            return;
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast.dismiss(loadingToastId);
          showError('Failed to upload image. Please try again.');
          return;
        }
      }

      const serviceData = {
        industry: serviceFormData.selectedIndustry,
        name: serviceFormData.name.trim(),
        description: serviceFormData.description.trim(),
        isActive: serviceFormData.status === 'active',
        tags: serviceFormData.tags,
        imageUrl: imageUrl,
      };

      await api.post('/industryListing/service-types', serviceData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      await fetchIndustryAndServices();

      toast.dismiss(loadingToastId);
      showSuccess('Service added successfully!', { id: TOAST_IDS.ADD_SERVICE });
      setIsModalOpen(false);
      resetFormData();
    } catch (error) {
      console.error('Error adding service:', error);
      toast.dismiss(loadingToastId);

      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          showError(data.message || 'Invalid data. Please check your inputs.');
        } else {
          showError(data?.message || 'Failed to add service');
        }
      } else {
        showError('Network error. Please check your connection and try again.');
      }
    }
  }, [serviceFormData, fetchIndustryAndServices, resetFormData]);

  const handleUpdateService = useCallback(async () => {
    if (!selectedService) return;

    if (!serviceFormData.name.trim()) {
      showError('Service name is required');
      return;
    }

    if (!serviceFormData.selectedIndustry) {
      showError('Please select an industry');
      return;
    }

    const loadingToastId = showLoading('Updating service...', {
      id: TOAST_IDS.UPDATE_SERVICE,
    });

    try {
      let imageUrl = serviceFormData.previewImage;

      if (serviceFormData.image && serviceFormData.image instanceof File) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', serviceFormData.image);

          const uploadResponse = await api.post(`/upload/file`, imageFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          imageUrl = uploadResponse.data.secure_url || uploadResponse.data.url;

          if (!imageUrl) {
            console.error('Upload response:', uploadResponse.data);
            showError('Failed to get image URL from upload response');
            toast.dismiss(loadingToastId);
            return;
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast.dismiss(loadingToastId);
          showError('Failed to upload image. Please try again.');
          return;
        }
      }
      const serviceData = {
        industry: serviceFormData.selectedIndustry,
        name: serviceFormData.name.trim(),
        description: serviceFormData.description.trim(),
        isActive: serviceFormData.status === 'active',
        tags: serviceFormData.tags,
        imageUrl,
      };

      await api.patch(
        `/industryListing/service-types/${selectedService._id}`,
        serviceData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      await fetchIndustryAndServices();

      toast.dismiss(loadingToastId);
      showSuccess('Service updated successfully!', {
        id: TOAST_IDS.UPDATE_SERVICE,
      });
      setIsModalOpen(false);
      setSelectedService(null);
      resetFormData();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.dismiss(loadingToastId);

      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          showError(data.message || 'Invalid data. Please check your inputs.');
        } else {
          showError(data?.message || 'Failed to update service');
        }
      } else {
        showError('Network error. Please check your connection and try again.');
      }
    }
  }, [
    selectedService,
    serviceFormData,
    fetchIndustryAndServices,
    resetFormData,
  ]);

  const handleDeleteService = useCallback(async (serviceId) => {
    showConfirmToast({
      message:
        'Are you sure you want to delete this service? This action cannot be undone!',
      toastId: `${TOAST_IDS.CONFIRM_DELETE}-${serviceId}`,
      onConfirm: async () => {
        const loadingToastId = showLoading('Deleting service...', {
          id: TOAST_IDS.DELETE_SERVICE,
        });
        try {
          await api.delete(`/industryListing/service-types/${serviceId}`);
          setServices((prev) =>
            prev.filter((service) => service._id !== serviceId),
          );
          toast.dismiss(loadingToastId);
          showSuccess('Service deleted successfully!', {
            id: TOAST_IDS.DELETE_SERVICE,
          });
        } catch (error) {
          console.error('Error deleting service:', error);
          toast.dismiss(loadingToastId);
          showError(
            error.response?.data?.message || 'Failed to delete service',
            {
              id: TOAST_IDS.DELETE_SERVICE,
            },
          );
        }
      },
      onCancel: () => toast.dismiss(`${TOAST_IDS.CONFIRM_DELETE}-${serviceId}`),
    });
  }, []);

  const handleEditService = useCallback((action, industry, service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  }, []);

  const filteredServices = services.filter((service) =>
    service.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:ml-64 w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] font-nohemi font-light">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-nowrap">
          <Title title="Service Types" subtitle={industry?.name} />
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => {
              setIsModalOpen(true);
              setSelectedService(null);
              resetFormData();
            }}
            className="px-4 py-2 text-xs text-white bg-black rounded-md hover:bg-black"
          >
            Add Service
          </button>
          <button
            onClick={() => navigate('/industry-listing')}
            className="px-4 py-2 text-xs text-white bg-black rounded-md hover:bg-black"
          >
            Back to Industries
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="h-screen block bg-[#FFFFFF] rounded-xl p-4">
        {/* Search Bar */}
        <div className="flex justify-start">
          <form
            className="max-w-md mx-8 my-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative">
              <div className="absolute inset-y-0 flex items-center pointer-events-none start-0 ps-3">
                <CiSearch />
              </div>
              <input
                type="search"
                className="block w-[260px] h-[40px] p-4 ps-10 text-xs text-[#000000] rounded-[4px] bg-[#F5F6F7] focus:outline-none focus:ring-0 placeholder:text-[11px]"
                placeholder="Search Services"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3 lg:grid-cols-4">
          {filteredServices.length > 0 ? (
            filteredServices.map((service) => (
              <ServiceType
                key={service._id}
                service={service}
                industryId={industryId}
                onDelete={handleDeleteService}
                onEdit={handleEditService}
              />
            ))
          ) : (
            <div className="col-span-4 py-8 text-center text-gray-500">
              {loading ? 'Loading services...' : 'No services found'}
            </div>
          )}
        </div>
      </div>

      {/* Service Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="mb-6">
              <h2 className="mb-4 text-xl font-semibold">
                {selectedService ? 'Edit Service' : 'Add New Service'}
              </h2>

              {/* Image Upload */}
              <div className="flex justify-center mb-6">
                <div className="flex flex-col items-center">
                  <label className="cursor-pointer">
                    <div className="flex items-center justify-center w-24 h-24 overflow-hidden bg-gray-100 border-2 border-gray-300 border-dashed rounded-full">
                      {serviceFormData.previewImage ? (
                        <img
                          src={serviceFormData.previewImage}
                          alt="Preview"
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <BsCamera className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <input
                      type="file"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                    />
                    <p className="text-xs text-[#202224] mt-2 text-center">
                      Upload Photo
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1 text-center">
                      Max size: 5MB
                    </p>
                  </label>
                  {serviceFormData.image && (
                    <p className="mt-1 text-xs text-green-600">
                      {serviceFormData.image.name} selected
                    </p>
                  )}
                  {serviceFormData.previewImage && !serviceFormData.image && (
                    <p className="mt-1 text-xs text-blue-600">Current image</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-xs">Industry *</label>
                  <select
                    value={serviceFormData.selectedIndustry}
                    onChange={(e) =>
                      setServiceFormData((prev) => ({
                        ...prev,
                        selectedIndustry: e.target.value,
                      }))
                    }
                    className="w-full p-3 border rounded-md bg-gray-50"
                    disabled={!!industryId}
                  >
                    <option value="">Select Industry</option>
                    {allCompanies.map((ind) => (
                      <option key={ind._id} value={ind._id}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-xs">Service Name *</label>
                  <input
                    type="text"
                    value={serviceFormData.name}
                    onChange={(e) =>
                      setServiceFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full p-3 border rounded-md bg-gray-50"
                    placeholder="Service name"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-xs">Status</label>
                  <div className="flex gap-4">
                    <label className="flex items-center text-xs">
                      <input
                        type="radio"
                        checked={serviceFormData.status === 'active'}
                        onChange={() =>
                          setServiceFormData((prev) => ({
                            ...prev,
                            status: 'active',
                          }))
                        }
                        className="mr-2 accent-black"
                      />
                      Active
                    </label>
                    <label className="flex items-center text-xs">
                      <input
                        type="radio"
                        checked={serviceFormData.status === 'inactive'}
                        onChange={() =>
                          setServiceFormData((prev) => ({
                            ...prev,
                            status: 'inactive',
                          }))
                        }
                        className="mr-2 accent-black"
                      />
                      Inactive
                    </label>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block mb-2 text-xs">Description</label>
                <textarea
                  value={serviceFormData.description}
                  onChange={(e) =>
                    setServiceFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full p-3 border rounded-md bg-gray-50 min-h-[100px]"
                  placeholder="Service description"
                />
              </div>

              {/* Tags */}
              <div className="mb-6">
                <label className="block mb-2 text-xs">Tags</label>
                <input
                  type="text"
                  value={serviceFormData.newTag}
                  onChange={(e) =>
                    setServiceFormData((prev) => ({
                      ...prev,
                      newTag: e.target.value,
                    }))
                  }
                  onKeyDown={handleAddTag}
                  className="w-full p-3 mb-2 border rounded-md bg-gray-50"
                  placeholder="Add tags (press Enter or comma)"
                />
                <div className="flex flex-wrap gap-2">
                  {serviceFormData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center px-3 py-1 text-xs bg-gray-200 rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4">
              <button
                onClick={
                  selectedService ? handleUpdateService : handleAddService
                }
                className="flex-1 py-3 text-white bg-black rounded-md hover:bg-gray-800"
              >
                {selectedService ? 'Update Service' : 'Add Service'}
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedService(null);
                  resetFormData();
                }}
                className="flex-1 py-3 text-gray-800 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTypePage;
